import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Link2 } from "react-feather";
import toast from "react-hot-toast";
import QuotationCreateModal from "../../../sales/quotations/components/QuotationCreateModal";
import { emptyRow } from "../../../stock/components/LineItemsEditor";
import { useLazyGetProductsQuery } from "../../../masters/products/productsApi";
import { useLazyGetQuotationPrototypeQuery } from "../../../sales/quotations/quotationsApi";
import { QUOTATION_VAT_PERCENT } from "../../../sales/quotations/quotationVat";
import { useLinkSurveyQuotationMutation } from "../surveysApi";
import { paths } from "../../../../routes/paths";

const escapeHtml = (s) =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

// The quotation's Footer Notes field is rich text (HTML) - survey number first,
// then the requirements as typed, line breaks kept.
const buildRemarks = (survey) => {
  const parts = [`<p><strong>Site survey ${escapeHtml(survey.surveyNumber)}</strong></p>`];
  if (survey.requirements?.trim()) {
    parts.push(`<p>${escapeHtml(survey.requirements.trim()).replace(/\r?\n/g, "<br>")}</p>`);
  }
  return parts.join("");
};

/**
 * Looks each survey product up in the Product Master of the survey's branch -
 * the same list the quotation form's own product picker reads. A product is
 * identified by (productBranchId, productCode): a code is only unique within
 * its home branch, and a branch's list also returns products mapped in from
 * other branches.
 *
 * Rate (2026-10-28, customer: "automatically pick price from quote prototype"):
 * the survey's prototype line for the same product code first, then the
 * product's own salesRate (what a manual pick brings), else blank for the user.
 * VAT is the fixed quotation rate on every line, never the product's own.
 */
const useBuildQuotationRows = () => {
  const [searchProducts] = useLazyGetProductsQuery();
  const [fetchPrototype] = useLazyGetQuotationPrototypeQuery();
  return async (survey) => {
    // product code -> the prototype's rate (first PRODUCT line wins).
    const prototypeRates = new Map();
    // Batch224 group lines: template line number -> rate, plus "g:<groupId>" -> first rate.
    const groupRates = new Map();
    let prototypeFailed = false;
    if (survey.prototypeRecordGuid) {
      try {
        const prototype = await fetchPrototype(survey.prototypeRecordGuid).unwrap();
        (prototype?.lines || []).forEach((pl) => {
          if (pl.lineType === "PRODUCT" && pl.productCode != null) {
            const code = String(pl.productCode);
            if (!prototypeRates.has(code)) prototypeRates.set(code, pl.rate);
          } else if (pl.lineType === "FREETEXT" && pl.productGroupId != null) {
            groupRates.set(`n:${pl.lineNumber}:${pl.productGroupId}`, pl.rate);
            if (!groupRates.has(`g:${pl.productGroupId}`)) groupRates.set(`g:${pl.productGroupId}`, pl.rate);
          }
        });
      } catch {
        prototypeFailed = true; // falls back to the product master rates
      }
    }

    // The product list searches name and Product_Code_New, never Product_Code (Systems
    // 2741 is "PVCB-11"), so each product is searched by its name and matched on code.
    const searches = new Map(); // code -> search text
    (survey.lines || []).forEach((l) => {
      const code = String(l.productCode);
      if (!searches.has(code)) searches.set(code, l.description || code);
    });
    const found = new Map(); // "branchId:code" -> product, plus "code" -> first hit
    await Promise.all(
      [...searches.entries()].map(async ([code, term]) => {
        try {
          const matches = await searchProducts({ branchId: survey.branchId, search: term }).unwrap();
          (matches || [])
            .filter((p) => String(p.productCode) === code)
            .forEach((p) => {
              found.set(`${p.branchId}:${code}`, p);
              if (!found.has(code)) found.set(code, p);
            });
        } catch {
          // a failed lookup leaves that line without a rate - flagged below
        }
      })
    );

    let missing = 0;
    let noRate = 0;
    const rows = (survey.lines || []).map((l, i) => {
      const code = String(l.productCode);
      const product = found.get(`${l.productBranchId ?? survey.branchId}:${code}`) || found.get(code);
      if (!product) missing += 1;
      const rate = prototypeRates.has(code) ? prototypeRates.get(code) : product?.salesRate;
      if (rate == null) noRate += 1;
      return {
        ...emptyRow(),
        key: `survey-${i}-${Date.now()}`,
        lineType: "PRODUCT",
        productCode: l.productCode,
        productName: l.description || product?.productName || "",
        productGroupId: product?.productGroupId ?? "",
        productGroupName: product?.productGroupName ?? "",
        unit: l.unit || product?.unitLabel || "",
        qty: String(l.qty),
        rate: rate != null ? String(rate) : "",
        vatPercent: String(QUOTATION_VAT_PERCENT),
      };
    });
    // Group lines become group-tagged free-text lines, as the template itself has them.
    (survey.groupLines || []).forEach((g, i) => {
      const rate = groupRates.get(`n:${g.lineNumber}:${g.productGroupId}`) ?? groupRates.get(`g:${g.productGroupId}`);
      if (rate == null) noRate += 1;
      rows.push({
        ...emptyRow(),
        key: `survey-g-${i}-${Date.now()}`,
        lineType: "FREETEXT",
        isGroupLine: true,
        productCode: "",
        productName: g.description || g.groupName || "",
        productGroupId: String(g.productGroupId),
        productGroupName: g.groupName || "",
        unit: g.unit || "",
        qty: String(g.qty),
        rate: rate != null ? String(rate) : "",
        vatPercent: String(QUOTATION_VAT_PERCENT),
      });
    });
    return { rows, missing, noRate, prototypeFailed };
  };
};

/**
 * "Create Quotation" for a COMPLETED survey (detail.canConvert): opens the existing
 * quotation create form pre-filled from the survey, and once the quotation is saved
 * links it back (PUT /technical/surveys/{guid}/quotation -> QUOTED).
 * A QUOTED survey shows its quotation number linking to the quotation instead.
 */
const SurveyQuotationAction = ({ survey }) => {
  const buildRows = useBuildQuotationRows();
  const [linkQuotation, { isLoading: isLinking }] = useLinkSurveyQuotationMutation();
  const [prefill, setPrefill] = useState(null);
  const [isPreparing, setIsPreparing] = useState(false);
  // A quotation saved but not yet linked (the link call failed) - offered for retry.
  const [unlinked, setUnlinked] = useState(null);

  if (survey.status === "QUOTED" && survey.quotationRecordGuid) {
    return (
      <Link to={paths.quotationDetail(survey.quotationRecordGuid)} className="btn btn-outline-success d-inline-flex align-items-center">
        <FileText size={15} className="me-1" /> Quotation {survey.quotationNumber}
      </Link>
    );
  }
  if (!survey.canConvert) return null;

  const link = async (quotation) => {
    try {
      await linkQuotation({ recordGuid: survey.recordGuid, quotationRecordGuid: quotation.recordGuid }).unwrap();
      setUnlinked(null);
      toast.success(`Survey linked to quotation ${quotation.quotationNumber || ""}`.trim());
    } catch (e) {
      setUnlinked(quotation);
      toast.error(e?.message || "The quotation was saved but could not be linked to this survey");
    }
  };

  const open = async () => {
    setIsPreparing(true);
    try {
      const { rows, missing, noRate, prototypeFailed } = await buildRows(survey);
      if (prototypeFailed) toast.error("Could not read the survey's prototype - rates are from the product master");
      if (missing > 0) {
        toast.error(`${missing} material${missing === 1 ? " was" : "s were"} not found in the product master - check the rate`);
      }
      if (noRate > 0) {
        toast.error(`${noRate} line${noRate === 1 ? " has" : "s have"} no price on the prototype or product master - enter the rate`);
      }
      setPrefill({
        branchId: survey.branchId,
        customerOption:
          !survey.isProspect && survey.custId
            ? {
                value: `${survey.customerBranchId}-${survey.custId}`,
                label: survey.customerName,
                customer: { custId: survey.custId, branchId: survey.customerBranchId },
              }
            : null,
        salesReference: null,
        prototypeRecordGuid: survey.prototypeRecordGuid || "",
        remarks: buildRemarks(survey),
        rows,
      });
    } finally {
      setIsPreparing(false);
    }
  };

  return (
    <>
      <button type="button" className="btn btn-success d-inline-flex align-items-center" onClick={open} disabled={isPreparing}>
        <FileText size={15} className="me-1" /> {isPreparing ? "Preparing..." : "Create Quotation"}
      </button>
      {unlinked && (
        <button type="button" className="btn btn-outline-warning d-inline-flex align-items-center" onClick={() => link(unlinked)} disabled={isLinking}>
          <Link2 size={15} className="me-1" /> Link quotation {unlinked.quotationNumber || ""}
        </button>
      )}
      <QuotationCreateModal
        show={Boolean(prefill)}
        onClose={() => setPrefill(null)}
        prefill={prefill}
        stayOnPage
        notice={
          survey.isProspect
            ? `This survey is for a new customer (${survey.prospectName || survey.customerName}) - create the customer first, then choose them in the quotation.`
            : `Pre-filled from site survey ${survey.surveyNumber}. Check the rates before submitting.`
        }
        onCreated={link}
      />
    </>
  );
};

export default SurveyQuotationAction;
